/**
 * LiveRacePageV2 Component
 * Tarawera-inspired live results page
 *
 * Layout: NavHeader → RaceInfoBar → ElevationProfile → RunnerList (full width)
 * Dark header sections + Light content area
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRaceData } from '../../hooks/useRaceData';
import { useRunnerUpdates } from '../../hooks/useRunnerUpdates';
import { useFollowedRunners } from '../../hooks/useFollowedRunners';
import { useSimulation } from '../../hooks/useSimulation';
import { fetchRunnerDetail } from '../../lib/queries';
import { formatLocalDate } from '../../lib/timeFormatter';
import type { RunnerDetail } from '../../lib/types';

import { RaceHeaderV2 } from './RaceHeaderV2';
import { RunnerListV2 } from './RunnerListV2';
import { ElevationProfileV2 } from './ElevationProfileV2';
import { RunnerPopupV2 } from './RunnerPopupV2';
import { TestModeBanner } from '../TestModeBanner';
import { SiteFooter } from '../SiteFooter';

export function LiveRacePageV2({ mode = 'live' }: { mode?: 'live' | 'test' }) {
  const { slug } = useParams<{ slug: string }>();

  const { race, stations, loading: raceLoading, error: raceError } = useRaceData(slug || '');

  const {
    runners,
    loading: runnersLoading,
    lastUpdated,
    refetch,
  } = useRunnerUpdates(race?.id || null, { enabled: !!race, includeFeed: false });

  // AD-195: RD-driven simulation state (null when the race isn't being
  // replayed). Powers the header clock + SIMULATION chip, and the two
  // refetch effects below.
  const { simulation } = useSimulation(race?.id || null);

  // A teleport (scrubbing the clock to a new position) changes which rows
  // the anon RLS policy lets us SEE — but it does so without any
  // checkin_events INSERT, so the checkin_events Realtime subscription
  // inside useRunnerUpdates never fires. Explicitly refetch whenever the
  // simulation's anchor/playing/speed changes so newly-(in)visible rows
  // appear promptly. NOTE: we do NOT filter events client-side here — the
  // RLS predicate (server_timestamp <= sim_now(race_id)) is the only
  // filter; this effect just asks for a fresh read after the server-side
  // visibility boundary has moved.
  useEffect(() => {
    if (race) void refetch();
  }, [simulation?.anchor_sim_time, simulation?.anchor_wall_time, simulation?.playing, simulation?.speed]);

  // While PLAYING, sim_now() keeps advancing every second even though
  // nothing writes to race_simulations (no ticking timer — see simClock.ts).
  // That means rows become visible as sim_now sweeps past their
  // server_timestamp with, again, no INSERT to trigger Realtime. Poll every
  // 10s while playing so the page keeps moving during simulated playback.
  useEffect(() => {
    if (!simulation?.playing) return;
    const t = setInterval(() => void refetch(), 10_000);
    return () => clearInterval(t);
  }, [simulation?.playing, refetch]);

  const { followedBibs, toggleFollow } = useFollowedRunners(slug || '');

  // UI state
  const [search, setSearch] = useState('');
  const [myRunnersOnly, setMyRunnersOnly] = useState(false);

  // Runner popup
  const [popupRunnerId, setPopupRunnerId] = useState<string | null>(null);
  const [popupRunnerDetail, setPopupRunnerDetail] = useState<RunnerDetail | null>(null);

  // My Runners toggle affects both the table AND elevation bubble counts.
  // Search only filters the table (so the user doesn't lose the chart context while typing).
  const myRunnersFiltered = useMemo(() => {
    if (!myRunnersOnly) return runners;
    return runners.filter((r) => followedBibs.has(r.bib));
  }, [runners, myRunnersOnly, followedBibs]);

  const filteredRunners = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return myRunnersFiltered;
    return myRunnersFiltered.filter((r) => {
      const fullName = `${r.first_name} ${r.last_name}`.toLowerCase();
      return r.bib.toLowerCase().includes(query) || fullName.includes(query);
    });
  }, [myRunnersFiltered, search]);

  // Handle runner click - show popup
  const handleRowClick = useCallback(async (runnerId: string) => {
    if (!race) return;
    setPopupRunnerId(runnerId);
    setPopupRunnerDetail(null);

    try {
      const detail = await fetchRunnerDetail(runnerId, race.id);
      setPopupRunnerDetail(detail);
    } catch (error) {
      console.error('Error fetching runner detail:', error);
    }
  }, [race]);

  const handleClosePopup = useCallback(() => {
    setPopupRunnerId(null);
    setPopupRunnerDetail(null);
  }, []);

  // Get the runner summary for popup display while loading detail
  const popupRunner = useMemo(
    () => runners.find(r => r.id === popupRunnerId) || null,
    [runners, popupRunnerId]
  );

  // Error state
  if (raceError) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
            Race Not Found
          </h1>
          <p style={{ color: 'var(--v2-text-secondary)' }}>
            The race "{slug}" could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (raceLoading || !race) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--v2-text-secondary)' }}>Loading race data...</div>
      </div>
    );
  }

  // Test mode: hide results on /live/ when test_mode is true
  if (mode === 'live' && race.test_mode) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 16 }}>
            {race.name}
          </h1>
          <p style={{ color: 'var(--v2-text-secondary)', fontSize: 18, marginBottom: 8 }}>
            Race has not started yet.
          </p>
          <p style={{ color: 'var(--v2-text-secondary)' }}>
            Check back on{' '}
            <span style={{ fontWeight: 600, color: 'var(--v2-text-primary)' }}>
              {formatLocalDate(race.date)}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="v2-tarawera">
      {mode === 'test' && <TestModeBanner />}

      <RaceHeaderV2
        race={race}
        lastUpdated={lastUpdated}
        runners={runners}
        myRunnersActive={myRunnersOnly}
        onToggleMyRunners={() => setMyRunnersOnly((v) => !v)}
        simulation={simulation}
      />

      {/* Elevation Profile */}
      <ElevationProfileV2
        data={race?.elevation_profile ?? null}
        stations={stations}
        runners={myRunnersFiltered}
        followedBibs={followedBibs}
        onToggleFollow={toggleFollow}
      />

      {/* Runner List with integrated search */}
      {runnersLoading && runners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--v2-text-muted)', background: 'var(--v2-bg-card)' }}>
          Loading runners...
        </div>
      ) : (
        <RunnerListV2
          runners={filteredRunners}
          stations={stations}
          onRowClick={handleRowClick}
          raceTimeZone={race.time_zone}
          followedBibs={followedBibs}
          onToggleFollow={toggleFollow}
          search={search}
          onSearchChange={setSearch}
          raceSlug={slug || ''}
        />
      )}

      {/* Runner Popup */}
      {popupRunnerId && popupRunner && (
        <RunnerPopupV2
          runner={popupRunner}
          detail={popupRunnerDetail}
          raceSlug={slug || ''}
          raceTimeZone={race.time_zone || 'America/Los_Angeles'}
          onClose={handleClosePopup}
        />
      )}

      <SiteFooter />
    </div>
  );
}
